<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, shallowRef, watch } from "vue";
import {storeToRefs} from "pinia";
import useStore from "../store.ts";
import type {Playlist} from "../playlist/Playlist.ts";
import { EffectType, type Component, type EffectDirection, type RenderEffect } from "../types.ts";
import { isScheduled, parseStep, type RawStep, type Step } from "../playlist/PlaylistSteps.ts";
import {locateStepByEpoch, toEpochSeconds} from "../utils/DateUtils.ts";
import { effectTimeToMs } from "../utils/ElementUtils.ts";
import type { SwitchVariant } from "../switching/types";

const props = defineProps<{
  item: Component
}>();

/** ---- Store & reactive state ---- */
const store = useStore();
const {currentDate} = storeToRefs(store);

const steps = ref<Step[]>([]);
/** `orderedSteps` whose schedule allows them now; the rotation runs over these only. */
const activeSteps = ref<Step[]>([]);
const currentStep = shallowRef<Step | null>(null);
const previousStep = shallowRef<Step | null>(null);
const nextStep = shallowRef<Step | null>(null);
const totalLength = ref(0);

const token = ref(0);
const currentState = ref<"enter" | "active">("active");
const previousState = ref<"active" | "exit">("exit");
// see docs/EXTRACTED_COMMENTS.md#c806
const enterVariant = ref<SwitchVariant>("crossfade");
const exitVariant = ref<SwitchVariant>("none");
const enterDirection = ref<EffectDirection | undefined>(undefined);
const exitDirection = ref<EffectDirection | undefined>(undefined);
const switchInMs = ref(0);
const switchOutMs = ref(0);
const switchEase = ref("linear");

let playlistInterval: ReturnType<typeof setInterval> | null = null;
let fallbackTimer: number | undefined;
// see docs/EXTRACTED_COMMENTS.md#c048
let registeredPlaylistSrc: string | null = null;

const id = props.item.props.id;

/** ---- Helpers ---- */
function cloneStep<T>(obj: T): T {
  return JSON.parse(JSON.stringify(obj));
}

function clearFallback() {
  if (fallbackTimer !== undefined) {
    clearTimeout(fallbackTimer);
    fallbackTimer = undefined;
  }
}

function sumDurationMs(effects: RenderEffect[] | undefined): number {
  if (!effects || effects.length === 0) return 0;
  let total = 0;
  for (const eff of effects) total += effectTimeToMs(eff.effect_time);
  return total;
}

// see docs/EXTRACTED_COMMENTS.md#c806
function layerVariant(effects: RenderEffect[] | undefined): SwitchVariant | null {
  switch (effects?.[0]?.effect_type) {
    case EffectType.CROSS_FADE:
      return "crossfade";
    case EffectType.FLY:
      return "fly";
    case EffectType.TURN:
      return "turn";
    case EffectType.CURTAIN:
      return "curtain";
    default:
      return null; // IN / OUT placeholders, absent list: this layer does not animate
  }
}

function stepKey(step: Step | null): string {
  if (!step) return "";
  return `${step.sequence}.${step.sub_sequence}`;
}

function findNextOrderedStep(step: Step | null): Step | null {
  if (!step || activeSteps.value.length <= 1) return null;
  const key = stepKey(step);
  const idx = activeSteps.value.findIndex((candidate) => stepKey(candidate) === key);
  if (idx < 0) return null;
  const nextIdx = (idx + 1) % activeSteps.value.length;
  const candidate = activeSteps.value[nextIdx];
  if (!candidate) return null;
  if (stepKey(candidate) === key) return null;
  return candidate;
}

function refreshPreloadedNext(step: Step | null) {
  const next = findNextOrderedStep(step);
  if (!next) {
    nextStep.value = null;
    return;
  }
  nextStep.value = cloneStep(next);
}

function finalizeTransition(expectedToken: number) {
  if (token.value !== expectedToken) return;
  clearFallback();
  previousStep.value = null;
  previousState.value = "exit";
  currentState.value = "active";
}

function startTransition(nextStep: Step, prevStep: Step, enterV: SwitchVariant, exitV: SwitchVariant, inMs: number, outMs: number, inDir?: EffectDirection, outDir?: EffectDirection) {
  const t = ++token.value;
  clearFallback();

  currentStep.value = nextStep;
  previousStep.value = prevStep;
  enterVariant.value = enterV;
  exitVariant.value = exitV;
  enterDirection.value = inDir;
  exitDirection.value = outDir;
  switchInMs.value = Math.max(0, Math.floor(inMs));
  switchOutMs.value = Math.max(0, Math.floor(outMs));

  currentState.value = switchInMs.value > 0 ? "enter" : "active";
  previousState.value = "active";

  void nextTick().then(() => {
    requestAnimationFrame(() => {
      if (token.value !== t) return;
      requestAnimationFrame(() => {
        if (token.value !== t) return;
        if (currentState.value === "enter") currentState.value = "active";
        previousState.value = "exit";
      });
    });
  });

  // see docs/EXTRACTED_COMMENTS.md#c346 — a held layer (#c806) has no exit of its own; it lives for the enter.
  const exitMs = exitV === "none" ? switchInMs.value : switchOutMs.value;
  fallbackTimer = window.setTimeout(() => finalizeTransition(t), Math.max(0, exitMs) + 75);
}

function onPrevTransitionEnd(e: TransitionEvent) {
  if (!previousStep.value) return;
  if (e.target !== e.currentTarget) return;
  const el = e.currentTarget as HTMLElement;
  const raw = el.dataset.switchToken;
  const t = Number(raw);
  if (!Number.isFinite(t)) return;
  if (t !== token.value) return;
  if (previousState.value !== "exit") return;
  finalizeTransition(t);
}

/** ---- Playlist flow ---- */
const receivePlaylist = (playlist: Playlist) => {
  const data = playlist.getValue();
  if (!data?.step) return;
  steps.value = (data.step as RawStep[]).map((s) => parseStep(s, id, props.item.props.style));

  // Mark components to ignore render
  steps.value.forEach((s) => {
    const pid = (s.props?.id as string) ?? (s.props?.item as any)?.props?.id;
    if (!pid) return;
    if (store.components?.[pid]) {
      store.components[pid].ignore_render = true;
    }
  });
  updateActiveSteps(new Date(currentDate.value));
  refreshPreloadedNext(currentStep.value);
};

const orderedSteps = computed(() =>
  [...steps.value].sort((a, b) => {
    if (a.sequence !== b.sequence) return a.sequence - b.sequence;
    return a.sub_sequence - b.sub_sequence;
  })
);

/** Recompute the scheduled steps; returns whether the set changed. */
function updateActiveSteps(now: Date): boolean {
  const active = orderedSteps.value.filter((s) => isScheduled(s.schedule, now));
  const changed = active.length !== activeSteps.value.length
    || active.some((s, i) => s !== activeSteps.value[i]);
  if (changed) {
    activeSteps.value = active;
    totalLength.value = active.reduce((acc, s) => acc + s.length, 0);
  }
  return changed;
}

function clearSteps() {
  token.value++;
  clearFallback();
  currentStep.value = null;
  previousStep.value = null;
  nextStep.value = null;
  currentState.value = "active";
  previousState.value = "exit";
}

watch(
  () => props.item.published?.src,
  (src) => {
    if (registeredPlaylistSrc && registeredPlaylistSrc !== src) {
      store.playlistManager.getPlaylist(registeredPlaylistSrc)?.removeCallback(receivePlaylist);
      registeredPlaylistSrc = null;
    }
    if (!src) return;
    registeredPlaylistSrc = src;
    store.socketManager.requestPlaylist(src, receivePlaylist);
  },
  { immediate: true }
);

onMounted(() => {
  playlistInterval = setInterval(() => {
    if (updateActiveSteps(new Date(currentDate.value))) refreshPreloadedNext(currentStep.value);
    if (!activeSteps.value.length || !totalLength.value) {
      // nothing scheduled right now: the playlist shows nothing
      if (currentStep.value || previousStep.value || nextStep.value) clearSteps();
      return;
    }

    // Derive position purely from currentDate (epoch seconds)
    const nowSec = toEpochSeconds(currentDate.value);
    const res = locateStepByEpoch(nowSec, totalLength, activeSteps);
    if (res == null) return;
    const {step, remaining} = res;
    if (!step) return;

    // see docs/EXTRACTED_COMMENTS.md#c347
    const cloned = cloneStep(step);
    cloned.length = remaining; // time left within this step

    const prev = currentStep.value;

    // No previous step – just render immediately, no effects.
    if (!prev) {
      currentStep.value = cloned;
      previousStep.value = null;
      currentState.value = "active";
      previousState.value = "exit";
      clearFallback();
      refreshPreloadedNext(cloned);
      return;
    }

    // see docs/EXTRACTED_COMMENTS.md#c348
    if (stepKey(prev) === stepKey(cloned)) {
      currentStep.value = cloned;
      refreshPreloadedNext(cloned);
      return;
    }

    // see docs/EXTRACTED_COMMENTS.md#c349
    const isNewSequence = prev.sequence !== cloned.sequence;
    const isFirstSubSequence = cloned.sub_sequence === 1;
    const effects = props.item.effects;

    if (!effects || !isNewSequence || !isFirstSubSequence) {
      // see docs/EXTRACTED_COMMENTS.md#c350
      clearFallback();
      previousStep.value = null;
      currentStep.value = cloned;
      currentState.value = "active";
      previousState.value = "exit";
      refreshPreloadedNext(cloned);
      return;
    }

    const outEffects = effects.focus_change_out || [];
    const inEffects = effects.focus_change_in || [];
    const enterV = layerVariant(inEffects);
    const exitV = layerVariant(outEffects);
    const outMs = exitV ? sumDurationMs(outEffects) : 0;
    const inMs = enterV ? sumDurationMs(inEffects) : 0;
    startTransition(cloned, prev, enterV ?? "crossfade", exitV ?? "none", inMs, outMs, inEffects[0]?.direction, outEffects[0]?.direction);
    refreshPreloadedNext(cloned);
  }, 1000);
});

onBeforeUnmount(() => {
  if (playlistInterval !== null) clearInterval(playlistInterval);
  clearFallback();
  nextStep.value = null;
  if (registeredPlaylistSrc) {
    store.playlistManager.getPlaylist(registeredPlaylistSrc)?.removeCallback(receivePlaylist);
    registeredPlaylistSrc = null;
  }
});
</script>

<template>
  <div
    :id="id"
    class="lumids-switch-container lumids-switch-container--playlist"
    :style="{
      ...(props.item.props?.style ?? {}),
      '--lumids-switch-w': props.item.props?.style?.width ?? '100%',
      '--lumids-switch-h': props.item.props?.style?.height ?? '100%',
      '--lumids-switch-in-ms': `${Math.max(0, switchInMs)}ms`,
      '--lumids-switch-out-ms': `${Math.max(0, switchOutMs)}ms`,
      '--lumids-switch-ease': switchEase,
    }"
  >
    <div
      v-if="previousStep && previousStep.props"
      class="lumids-switch-layer lumids-switchable"
      :data-switch="previousState"
      :data-variant="exitVariant"
      :data-direction="exitDirection"
      :data-switch-token="String(token)"
      @transitionend="onPrevTransitionEnd"
    >
      <component
        v-if="previousStep.type"
        :is="previousStep.type"
        :key="`prev-${token}-${stepKey(previousStep)}-${previousStep?.props?.item?.published?.src ?? ''}`"
        v-bind="previousStep.props"
      />
    </div>

    <div
      v-if="currentStep && currentStep.props"
      class="lumids-switch-layer lumids-switchable"
      :data-switch="currentState"
      :data-variant="enterVariant"
      :data-direction="enterDirection"
    >
      <component
        v-if="currentStep.type"
        :is="currentStep.type"
        :key="`current-${token}-${stepKey(currentStep)}-${currentStep?.props?.item?.published?.src ?? ''}`"
        v-bind="currentStep.props"
      />
    </div>

    <div
      v-if="nextStep && nextStep.type"
      v-show="false"
      class="lumids-switch-layer lumids-switchable"
      :data-variant="enterVariant"
      aria-hidden="true"
    >
      <component
        :is="nextStep.type"
        :key="`next-${stepKey(nextStep)}-${nextStep?.props?.item?.published?.src ?? ''}`"
        v-bind="nextStep.props"
      />
    </div>
  </div>
</template>

<style scoped></style>
